// client/src/AuthContext.js
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { auth, firestore } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { registerDeviceLink } from './utils/deviceLink';
import api from './api';

const IS_PROD = process.env.NODE_ENV === 'production';

export const AuthContext = createContext({
  currentUser: null,
  userRole: null,
  loading: true,
  emailVerified: true,
  reloadEmailVerified: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userCGV, setUserCGV] = useState(null);
  const [loading, setLoading] = useState(true);
  // En dev : toujours true — en prod : déterminé par user.emailVerified (Firebase Auth)
  const [emailVerified, setEmailVerified] = useState(!IS_PROD);

  // Recharge le statut emailVerified depuis Firebase Auth.
  // forceRefresh=true : force un nouveau JWT (utilisé une fois après flag-verification)
  // forceRefresh=false : lecture cache (polling toutes les 3s, pas de disruption Firestore)
  const reloadEmailVerified = useCallback(async (forceRefresh = false) => {
    if (!IS_PROD || !auth.currentUser || auth.currentUser.isAnonymous) return;
    await auth.currentUser.reload();
    if (auth.currentUser.emailVerified) {
      // Forcer le rafraîchissement du JWT pour que Firestore reçoive email_verified=true
      await auth.currentUser.getIdToken(true);
      setEmailVerified(true);
      return;
    }
    const tokenResult = await auth.currentUser.getIdTokenResult(forceRefresh);
    const needsVerification = tokenResult.claims.needsEmailVerification === true;
    setEmailVerified(!needsVerification);
  }, []);

  useEffect(() => {
    let unsubscribeAuth = () => {};
    let unsubscribeRole = () => {};

    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // stop écoute précédente (changement d'utilisateur)
      unsubscribeRole();
      unsubscribeRole = () => {};

      // Pendant la transition, on masque les routes protégées
      setLoading(true);

      if (!user) {
        setCurrentUser(null);
        setUserRole(null);
        setUserCGV(null);
        setEmailVerified(!IS_PROD);
        try { localStorage.removeItem('userRole'); } catch {}
        setLoading(false);
        return;
      }

      setCurrentUser(user);
      // Lecture du claim needsEmailVerification (posé uniquement sur les nouveaux comptes)
      // Anciens comptes : claim absent → !needsVerification = true → considérés vérifiés
      let tokenResult = await user.getIdTokenResult(false);
      let needsVerification = tokenResult.claims.needsEmailVerification === true;

      // AUTO-HEAL en dev : le claim `needsEmailVerification` bloquerait les règles Firestore
      // puisqu'aucun email de vérif n'est envoyé en dev. On appelle /sync-claims qui le strip.
      if (!IS_PROD && needsVerification && !user.isAnonymous) {
        try {
          await api.post('/api/auth/sync-claims');
          await user.getIdToken(true); // refresh token pour récupérer les claims mis à jour
          tokenResult = await user.getIdTokenResult(false);
          needsVerification = tokenResult.claims.needsEmailVerification === true;
        } catch (e) {
          console.warn('[AuthContext] dev auto-heal claims failed:', e?.message);
        }
      }

      // Si l'email est vérifié côté Auth mais que le JWT cache a encore needsEmailVerification=true,
      // forcer le refresh pour que Firestore reçoive email_verified=true dans le token
      if (!user.isAnonymous && user.emailVerified && needsVerification) {
        await user.getIdToken(true);
      }
      // Anonymes, dev, anciens comptes (sans claim) : pas de vérification requise
      setEmailVerified(!IS_PROD || user.isAnonymous || user.emailVerified || !needsVerification);

      registerDeviceLink().catch(() => {});
      try {
        localStorage.setItem('ecobill_last_uid', user.uid);
        // Une connexion (anonyme ou officielle) lève le garde posé à la déconnexion
        localStorage.removeItem('ecobill_logout_guard');
      } catch {}

      // S'assurer que le doc user existe (anonymes inclus)
      const userRef = doc(firestore, 'users', user.uid);
      try {
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          // Création du doc en self — bloquée par les règles si rôle non-client (ex: cashier créé
          // côté serveur). On tente ; en cas d'échec, on compte sur le doc créé par le serveur.
          try {
            await setDoc(userRef, {
              uid: user.uid,
              role: 'client', // défaut pour parcours QR invité
              createdAt: serverTimestamp(),
            }, { merge: true });
          } catch (createErr) {
            console.warn('[AuthContext] self-create user doc refusé (probablement role non-client):', createErr?.code);
          }
        } else {
          const data = snap.data() || {};
          const updates = {};
          if (!data.role) updates.role = 'client';
          if (!data.uid) updates.uid = user.uid;
          if (!data.createdAt) updates.createdAt = serverTimestamp();
          // Backfill best-effort : si les règles refusent (ex: cashier sans email vérifié),
          // on n'interrompt pas le flux — le onSnapshot ci-dessous lira correctement le doc.
          if (Object.keys(updates).length > 0) {
            try {
              await setDoc(userRef, updates, { merge: true });
            } catch (backfillErr) {
              console.warn('[AuthContext] backfill user doc refusé:', backfillErr?.code);
            }
          }
        }

        // Écriture CGU si l'utilisateur vient de s'inscrire et a coché les CGU
        // (Signup.js et AffiliatesSignup.js posent ce flag dans sessionStorage)
        if (!user.isAnonymous) {
          try {
            const pendingRole = sessionStorage.getItem('ecobill_cgu_pending');
            if (pendingRole) {
              const snapData = snap.exists() ? (snap.data() || {}) : {};
              const cguUpdates = { CGU: 'accepted', CGUacceptedAt: serverTimestamp() };
              // Pour les clients : écrire aussi CGV pour éviter la double demande dans ClientDocs
              if (pendingRole === 'client' && !snapData.CGV) {
                cguUpdates.CGV = 'accepted';
              }
              await setDoc(userRef, cguUpdates, { merge: true });
              sessionStorage.removeItem('ecobill_cgu_pending');
            }
          } catch {}
        }

        // Écoute en temps réel du rôle
        unsubscribeRole = onSnapshot(
          userRef,
          (ds) => {
            const data = ds.data() || {};
            const role = data.role || 'client';
            setUserRole(role);
            setUserCGV(data.CGV || null);
            try { localStorage.setItem('userRole', role); } catch {}
            setLoading(false);
          },
          (err) => {
            console.warn('onSnapshot role error:', err);
            const cached = localStorage.getItem('userRole');
            setUserRole(cached || 'client');
            setUserCGV(null);
            setLoading(false);
          }
        );
      } catch (e) {
        console.error('Erreur création doc user (permission ?):', e);
        const cached = localStorage.getItem('userRole');
        setUserRole(cached || 'client');
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeRole();
    };
  }, []);

  const value = useMemo(
    () => ({ currentUser, userRole, userCGV, loading, emailVerified, reloadEmailVerified }),
    [currentUser, userRole, userCGV, loading, emailVerified, reloadEmailVerified]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
